/**
 * Invoice list — `GET payments/invoices/`, scoped to the caller by the
 * backend (buildScope: a landlord/agent sees their properties' invoices, a
 * tenant sees only their own). Status filter + free-text search are
 * server-side.
 *
 * "Add invoice" and "Generate monthly invoices" are hidden for TENANT — a
 * tenant's branch never mounts the `new`/`:id/edit` routes those actions
 * target. "Generate monthly invoices" is further restricted to LANDLORD/AGENT
 * only: `generate_monthly_invoices` uses `IsLandlordOrAgent`, which — unlike
 * the manager-role check on plain invoice CRUD — does NOT accept SUPERAGENT
 * (payments/routes.ts).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Column, Grid, InlineLoading, Select, SelectItem, TextInput } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { FileText } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { EmptyState } from '../../components/EmptyState';
import { Notify } from '../../components/carbon/Notify';
import { Pager } from '../../components/carbon/Pager';
import { PageHeader } from '../../components/carbon/PageHeader';
import { StatusTag } from '../../components/carbon/StatusTag';
import {
  ResponsiveDataTable,
  type Column as DataColumn,
} from '../../components/carbon/ResponsiveDataTable';
import { generateMonthlyInvoices, listInvoices } from '../../api/endpoints/payments';
import { INVOICE_STATUSES, type InvoiceListRow, type InvoiceStatus } from '../../types/payment';

export function InvoiceListPage(): React.JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isTenant = user?.role === 'TENANT';
  const canGenerateInvoices = user?.role === 'LANDLORD' || user?.role === 'AGENT';

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [search, setSearch] = useState('');
  const [property, setProperty] = useState('');

  const query = useQuery({
    queryKey: ['invoices', { page, status, search, property }],
    queryFn: () =>
      listInvoices({
        page,
        status: status === '' ? undefined : status,
        search: search === '' ? undefined : search,
        property: property === '' ? undefined : Number(property),
      }),
  });

  const generate = useMutation({
    mutationFn: generateMonthlyInvoices,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  });

  const invoices = query.data?.results ?? [];
  const count = query.data?.count ?? 0;

  const columns: readonly DataColumn<InvoiceListRow>[] = [
    { key: 'invoice_number', header: 'Invoice #', render: (row) => <Link to={`${row.id}`}>{row.invoice_number}</Link> },
    { key: 'tenant_name', header: 'Tenant', render: (row) => row.tenant_name },
    { key: 'unit_number', header: 'Unit', render: (row) => row.unit_number },
    { key: 'property_name', header: 'Property', render: (row) => row.property_name },
    { key: 'charge_type', header: 'Charge type', render: (row) => row.charge_type },
    { key: 'amount_due', header: 'Amount due', render: (row) => row.amount_due },
    { key: 'amount_paid', header: 'Amount paid', render: (row) => row.amount_paid },
    { key: 'due_date', header: 'Due date', render: (row) => row.due_date },
    { key: 'status', header: 'Status', render: (row) => <StatusTag status={row.status} /> },
  ];

  return (
    <section>
      <PageHeader
        title="Invoices"
        actions={
          <>
            {canGenerateInvoices && (
              <Button kind="tertiary" disabled={generate.isPending} onClick={() => generate.mutate()}>
                {generate.isPending ? <InlineLoading description="Generating…" /> : 'Generate monthly invoices'}
              </Button>
            )}
            {!isTenant && (
              <Button as={Link} to="new" renderIcon={Add}>
                Add invoice
              </Button>
            )}
          </>
        }
      />

      <p className="page-subnav">
        {!isTenant && <Link to="recurring-charges">Recurring charges</Link>}
        <Link to="payments">Payment history</Link>
        <Link to="ledger">Ledger</Link>
        <Link to="upcoming">Upcoming</Link>
        <Link to="overdue">Overdue</Link>
      </p>

      {generate.isSuccess && <Notify kind="success" title={generate.data.message} className="page-notify" />}
      {generate.isError && (
        <Notify kind="error" title={parseApiError(generate.error).message} className="page-notify" />
      )}

      <Grid
        className="filter-grid"
        as="form"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
      >
        <Column sm={4} md={3} lg={4}>
          <TextInput
            id="invoice-search"
            labelText="Search"
            placeholder="Tenant name or invoice number"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Column>
        <Column sm={4} md={2} lg={3}>
          <TextInput
            id="invoice-property"
            labelText="Property id"
            type="number"
            value={property}
            onChange={(event) => setProperty(event.target.value)}
          />
        </Column>
        <Column sm={4} md={2} lg={3}>
          <Select
            id="invoice-status"
            labelText="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as InvoiceStatus | '');
              setPage(1);
            }}
          >
            <SelectItem value="" text="All" />
            {INVOICE_STATUSES.map((value) => (
              <SelectItem key={value} value={value} text={value} />
            ))}
          </Select>
        </Column>
        <Column sm={4} md={1} lg={2} className="filter-grid__submit">
          <Button type="submit" kind="secondary">
            Apply
          </Button>
        </Column>
      </Grid>

      {query.isPending && <InlineLoading description="Loading invoices…" />}
      {query.isError && <Notify kind="error" title={parseApiError(query.error).message} />}

      {query.isSuccess && invoices.length === 0 && (
        <EmptyState
          icon={FileText}
          message="No invoices found."
          action={isTenant ? undefined : { label: 'Add invoice', to: 'new' }}
        />
      )}

      {query.isSuccess && invoices.length > 0 && (
        <>
          <ResponsiveDataTable caption="Invoices" columns={columns} rows={invoices} rowId={(row) => String(row.id)} />
          <Pager page={page} count={count} onPageChange={setPage} />
        </>
      )}
    </section>
  );
}

export default InvoiceListPage;
